// Shared user create/edit form. Renders into the empty <form id="user-form">
// shell that admin-user-new.html and admin-user-edit.html both ship; the
// presence of an ?id= query param decides which mode.
//
// Roles match UserRoles.cs: platform_admin / organization. Organization-role
// users must be paired with an OrganizationId - the form reveals the
// Organisatie picker when role=organization and hides it otherwise. The
// backend re-validates on submit.
import { api } from '../api';
import { initAdminShell, escapeHtml, showError, hideError, setButtonBusy } from './shell';
import { t } from '../i18n/i18n';

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  isBlocked: boolean;
  organizationId: string | null;
}

interface Organization {
  id: string;
  name: string;
}

const ROLE_OPTIONS = ['platform_admin', 'organization'] as const;

(async () => {
  const me = await initAdminShell('users').catch(() => null);
  if (!me) return;

  const id = new URL(window.location.href).searchParams.get('id');
  const editing = id !== null && id !== '';
  const form = document.getElementById('user-form') as HTMLFormElement | null;
  if (!form) return;

  // Organizations list - needed for the organization-role picker.
  let organizations: Organization[] = [];
  try {
    const client = await api();
    const { data } = await client.get<Organization[]>('/api/admin/organizations');
    organizations = data;
  } catch {
    // Picker stays empty; submit will fail validation if organization is chosen.
  }

  let initial: UserRow = {
    id: '', name: '', email: '', role: 'platform_admin',
    isBlocked: false, organizationId: null,
  };

  if (editing) {
    try {
      const client = await api();
      const { data } = await client.get<UserRow>(`/api/users/${encodeURIComponent(id!)}`);
      initial = data;
    } catch (err) {
      showError('form-error', err instanceof Error ? err.message : t('admin.user_form.err_load'));
      return;
    }
  }

  form.innerHTML = renderForm(initial, organizations, editing);
  form.hidden = false;

  // Toggle the organization picker visibility based on the role select.
  const roleSelect    = form.elements.namedItem('role') as HTMLSelectElement;
  const orgWrap       = form.querySelector<HTMLElement>('#user-owner-wrap');
  const orgSelect     = form.elements.namedItem('organizationId') as HTMLSelectElement;
  function syncOrganizationVisibility(): void {
    const isOrganization = roleSelect.value === 'organization';
    if (orgWrap) orgWrap.hidden = !isOrganization;
    if (orgSelect) orgSelect.required = isOrganization;
  }
  roleSelect.addEventListener('change', syncOrganizationVisibility);
  syncOrganizationVisibility();

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideError('form-error');
    const successBox = document.getElementById('form-success');
    if (successBox) successBox.hidden = true;

    const data = new FormData(form);
    const role = String(data.get('role') ?? '');
    const payload: Record<string, unknown> = {
      name: String(data.get('name') ?? '').trim(),
      role,
      organizationId: role === 'organization' ? String(data.get('organizationId') ?? '') : null,
    };
    if (!editing) {
      payload.email = String(data.get('email') ?? '').trim();
    } else {
      payload.isBlocked = data.get('isBlocked') === 'on';
    }

    const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    setButtonBusy(submitBtn, true);
    try {
      const client = await api();
      if (editing) {
        await client.put(`/api/users/${encodeURIComponent(id!)}`, payload);
        if (successBox) {
          successBox.textContent = t('admin.user_form.saved');
          successBox.hidden = false;
        }
      } else {
        const { data: result } = await client.post<{ email: string }>('/api/users', payload);
        if (successBox) {
          successBox.innerHTML = t('admin.user_form.created_html')
            .replace('{email}', `<strong>${escapeHtml(result.email)}</strong>`);
          successBox.hidden = false;
        }
        form.reset();
        syncOrganizationVisibility();
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err instanceof Error ? err.message : t('admin.user_form.err_save'));
      showError('form-error', msg);
    } finally {
      setButtonBusy(submitBtn, false);
    }
  });
})();

function renderForm(initial: UserRow, organizations: Organization[], editing: boolean): string {
  const roleOpts = ROLE_OPTIONS.map((r) => {
    const sel = r === initial.role ? ' selected' : '';
    return `<option value="${escapeHtml(r)}"${sel}>${escapeHtml(t(`admin.user_form.role_${r}`))}</option>`;
  }).join('');

  const orgOpts = organizations.map((o) => {
    const sel = o.id === initial.organizationId ? ' selected' : '';
    return `<option value="${escapeHtml(o.id)}"${sel}>${escapeHtml(o.name)}</option>`;
  }).join('');

  const emailField = editing
    ? `<label class="form-field" for="uf-email">
         <span>${escapeHtml(t('admin.user_form.label_email'))}</span>
         <input type="email" id="uf-email" name="email" value="${escapeHtml(initial.email)}" disabled />
       </label>`
    : `<label class="form-field form-field--required" for="uf-email">
         <span>${escapeHtml(t('admin.user_form.label_email'))}</span>
         <input type="email" id="uf-email" name="email" required autocomplete="off" />
       </label>`;

  const blockedField = editing
    ? `<label class="form-field form-field--check" for="uf-blocked">
         <input type="checkbox" id="uf-blocked" name="isBlocked" ${initial.isBlocked ? 'checked' : ''} />
         <span>${escapeHtml(t('admin.user_form.label_blocked'))}</span>
       </label>`
    : '';

  const submitLabel = editing ? t('admin.user_form.submit_edit') : t('admin.user_form.submit_new');

  return `
    <fieldset class="form-group">
      <legend>${escapeHtml(t('admin.user_form.section_identity'))}</legend>
      <label class="form-field form-field--required" for="uf-name">
        <span>${escapeHtml(t('admin.user_form.label_name'))}</span>
        <input type="text" id="uf-name" name="name" value="${escapeHtml(initial.name)}" required />
      </label>
      ${emailField}
    </fieldset>

    <fieldset class="form-group">
      <legend>${escapeHtml(t('admin.user_form.section_access'))}</legend>
      <label class="form-field form-field--required" for="uf-role">
        <span>${escapeHtml(t('admin.user_form.label_role'))}</span>
        <select id="uf-role" name="role" required>${roleOpts}</select>
      </label>
      <div id="user-owner-wrap" hidden>
        <label class="form-field form-field--required" for="uf-owner">
          <span>${escapeHtml(t('admin.user_form.label_owner'))}</span>
          <select id="uf-owner" name="organizationId">
            <option value="">${escapeHtml(t('admin.user_form.owner_placeholder'))}</option>
            ${orgOpts}
          </select>
        </label>
        <p class="form-hint" style="font-size:12px;color:var(--sk-muted);margin-top:-4px;">
          ${escapeHtml(t('admin.user_form.owner_hint'))}
        </p>
      </div>
      ${blockedField}
    </fieldset>

    <div class="form-actions">
      <button type="submit" class="btn btn--primary">${escapeHtml(submitLabel)}</button>
      <a href="/admin-users.html" class="btn-link">${escapeHtml(t('admin.actions.cancel'))}</a>
    </div>
  `;
}
